import React, { useEffect, useMemo, useState } from 'react';
import { AdminShell } from '../../components/AdminShell';
import { supabase } from '../../lib/supabase';
import { BOOKING_BASE_DOMAIN } from '../../lib/resolveShop';
import { Globe, CheckCircle2, XCircle, Loader2, ExternalLink } from 'lucide-react';

/**
 * Master admin — Custom Domain Readiness.
 *
 * One-click probe of the three layers a shop subdomain needs to work
 * end-to-end:
 *   1. DNS       — does `<sub>.instacut.co.uk` resolve?
 *   2. TLS       — does port 443 present a cert that covers this host?
 *   3. Ingress   — does GET / return the Instacut React bundle?
 *
 * Backed by GET /api/admin/domain-readiness on the master-admin-guarded
 * router. Results include a per-check `detail` string that already
 * spells out the fix, so this page is a very thin renderer.
 */
const DomainReadinessInner = () => {
  const [shops, setShops] = useState([]);
  const [selected, setSelected] = useState('');
  const [manual, setManual] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Populate the shops dropdown so the admin doesn't have to type a
  // known-good subdomain. Only pull `id + name + subdomain` — anything
  // else would be a leak that this page doesn't need.
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('shops')
        .select('id, name, subdomain')
        .order('name');
      const items = (data || []).filter((s) => s.subdomain);
      setShops(items);
      if (items.length && !selected) setSelected(items[0].subdomain);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const targetSub = useMemo(() => (manual.trim() || selected).toLowerCase(), [manual, selected]);

  const run = async () => {
    if (!targetSub) return;
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(
        `${BACKEND}/api/admin/domain-readiness?subdomain=${encodeURIComponent(targetSub)}`,
        { headers: { Authorization: `Bearer ${session?.access_token}` } },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <main className="main">
      <div className="topbar">
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Globe size={18} /> Custom domain readiness
          </h2>
          <div className="sub">
            Probe DNS / TLS / Ingress for a shop subdomain. Pick a shop or type
            any subdomain to test the full path a customer's browser walks.
          </div>
        </div>
      </div>

      <div className="body">
        <div className="panel" style={{ padding: 18, marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
                Existing shop
              </label>
              <select
                className="inp"
                value={selected}
                onChange={(e) => { setSelected(e.target.value); setManual(''); }}
                data-testid="domain-shop-select"
              >
                {shops.length === 0 && <option value="">(no shops with subdomain)</option>}
                {shops.map((s) => (
                  <option key={s.id} value={s.subdomain}>
                    {s.name} — {s.subdomain}.{BOOKING_BASE_DOMAIN}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
                Or type any subdomain
              </label>
              <input
                className="inp"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="e.g. testshop"
                data-testid="domain-manual-input"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
            <button
              className="btn p"
              onClick={run}
              disabled={running || !targetSub}
              data-testid="domain-run-check"
            >
              {running
                ? <><Loader2 size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} className="spin" /> Checking…</>
                : <>Run readiness check</>}
            </button>
            <span className="hint">
              Target: <code>{targetSub ? `${targetSub}.${BOOKING_BASE_DOMAIN}` : '—'}</code>
            </span>
          </div>

          {error && (
            <div className="statebox" style={{ marginTop: 14 }} data-testid="domain-error">
              <h5>Check failed to run</h5><p>{error}</p>
            </div>
          )}
        </div>

        {result && (
          <div className="panel" style={{ padding: 18, marginBottom: 14 }} data-testid="domain-result">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <b style={{ fontSize: 14 }}>Result for <code>{result.host}</code></b>
              {result.all_ok ? (
                <span className="pill green" data-testid="domain-all-ok">
                  <CheckCircle2 size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  All 3 layers reachable
                </span>
              ) : (
                <span className="pill amber" data-testid="domain-all-fail">Needs attention</span>
              )}
              {result.all_ok && (
                <a
                  href={`https://${result.host}/`}
                  target="_blank" rel="noreferrer"
                  className="hint"
                  style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  Open <ExternalLink size={11} />
                </a>
              )}
            </div>

            <CheckRow
              testid="check-dns"
              title="1. DNS resolution"
              hint="Wildcard A/CNAME record at your DNS provider."
              check={result.checks.dns}
            />
            <CheckRow
              testid="check-tls"
              title="2. HTTPS / TLS certificate"
              hint="Wildcard cert covering *.instacut.co.uk (Cloudflare Universal SSL when proxied)."
              check={result.checks.tls}
            />
            <CheckRow
              testid="check-ingress"
              title="3. Ingress routing"
              hint="Your host binds the wildcard hostname to this app (not just the apex)."
              check={result.checks.ingress}
            />
          </div>
        )}

        <div className="panel" style={{ padding: 18 }}>
          <b style={{ fontSize: 14 }}>Setup checklist for `*.{BOOKING_BASE_DOMAIN}`</b>
          <ol style={{ marginTop: 10, paddingLeft: 20, fontSize: 13, lineHeight: 1.65 }}>
            <li>
              <b>DNS.</b> Add a wildcard record at your DNS provider:
              <pre style={{ background: 'var(--card2)', padding: '6px 10px', borderRadius: 6,
                            marginTop: 4, fontSize: 12, overflow: 'auto' }}>
{`Type:    CNAME  (or A record with proxy)
Name:    *.${BOOKING_BASE_DOMAIN}
Value:   <your app origin>
Proxy:   Proxied (orange cloud in Cloudflare)`}
              </pre>
            </li>
            <li>
              <b>SSL.</b> Provision a wildcard certificate for <code>*.{BOOKING_BASE_DOMAIN}</code>.
              Cloudflare Universal SSL includes this for free when the domain
              is proxied. Otherwise you need Let's Encrypt DNS-01 or a
              purchased wildcard.
            </li>
            <li>
              <b>Ingress.</b> Ask your hosting provider to bind
              <code> *.{BOOKING_BASE_DOMAIN}</code> as a wildcard host to
              this app, not just the apex.
            </li>
            <li>
              <b>Verify.</b> Come back here, pick a shop, hit
              <em> Run readiness check</em>. All three rows must be green.
            </li>
          </ol>
        </div>
      </div>
    </main>
  );
};

const CheckRow = ({ testid, title, hint, check }) => {
  const ok = check?.ok;
  const Icon = ok ? CheckCircle2 : XCircle;
  const colour = ok ? 'var(--ok)' : 'var(--bad)';
  return (
    <div
      data-testid={testid}
      style={{
        display: 'grid',
        gridTemplateColumns: '20px 1fr auto',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 0',
        borderTop: '1px solid var(--line)',
      }}
    >
      <Icon size={16} style={{ color: colour, marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
        <div className="hint" style={{ fontSize: 11.5, marginTop: 2 }}>{hint}</div>
        <div style={{ fontSize: 12.5, marginTop: 4, color: colour }}>{check?.detail}</div>
      </div>
      <span className="hint" style={{ fontSize: 11 }}>{check?.took_ms}ms</span>
    </div>
  );
};

const DomainReadiness = () => (
  <AdminShell>
    <DomainReadinessInner />
  </AdminShell>
);
export default DomainReadiness;
